/**
 * useSessionId Hook
 * Derives game session context (team/session/activity) for the current gamer from the shared
 * upcoming-activity query. It reads through React Query (useGetUpcomingActivityForGamer) so every
 * consumer dedupes onto ONE cached request instead of each firing its own getUpcomingActivityForGamer.
 */

import { useMemo } from 'react'
import type { SprintActivityGameOptions } from '../../../pure-js/types/Sprint'
import { sanitizeSprintActivityGameOptions } from '../../../pure-js/libs/SprintActivityGameOptions'
import { useUser } from './useUser'
import { useGetUpcomingActivityForGamer } from '../apiLayer/queryHooks/queries'
import type { ActivityWithSession } from '../types/GeneralTypes'

interface UseSessionIdOptions {
  skip?: boolean
}

interface UseSessionIdResult extends SprintActivityGameOptions {
  sessionId: string | undefined
  teamId: string | undefined
  sprintId: string | undefined
  activityId: string | undefined
  activityType: string | undefined
  isLoading: boolean
}

/**
 * useSessionId - Gets session context from the shared upcoming-activity query.
 *
 * @param {UseSessionIdOptions} options - Hook options.
 * @return {UseSessionIdResult} Session context for the current user.
 */
export const useSessionId = (options: UseSessionIdOptions = {}): UseSessionIdResult => {
  const { skip = false } = options
  const { data: user } = useUser()
  const { data: response, isLoading } = useGetUpcomingActivityForGamer({ enabled: !skip && Boolean(user) })

  return useMemo<UseSessionIdResult>(() => {
    const activity = (response?.data as ActivityWithSession | undefined) ?? undefined

    if (!activity) {
      return {
        sessionId: undefined,
        teamId: undefined,
        sprintId: undefined,
        activityId: undefined,
        activityType: undefined,
        isLoading: skip ? false : isLoading
      }
    }

    // The user's team for this activity's sprint. Trust the backend's authoritative userTeamId (resolved
    // from the sprint participant, and the team the returned session is keyed to); fall back to the
    // intersection of the sprint's teams with the user's teams.
    const userTeamIds = user?.teamIds || []
    const teamId = activity.userTeamId || (activity.teamIds || []).find((tid: string) => userTeamIds.includes(tid))
    const session = activity.session && activity.session.teamId === teamId ? activity.session : undefined

    return {
      sessionId: session?.id,
      teamId,
      sprintId: activity.sprintId,
      activityId: activity.id,
      activityType: activity.type,
      ...sanitizeSprintActivityGameOptions(activity),
      isLoading: false
    }
  }, [response, user, skip, isLoading])
}
